// Nine-patch panel backgrounds sourced from the Kenney `ui.xml` sprite sheet
// (public/assets/sprites/ui.png). DOM panels are plain HTML, not Excalibur graphics, so the
// sheet is composited onto a small <canvas> layer rather than an ex.Sprite.
const UI_SHEET_URL = "/assets/sprites/ui.png";
// Coordinates from ui.xml: panel_beige.png / panel_beigeLight.png, both 100×100.
const PANEL_SOURCES = {
    beige: { x: 190, y: 100, width: 100, height: 100 },
    beigeLight: { x: 100, y: 376, width: 100, height: 100 },
};
// Nine-slice inset in source pixels — generous relative to the ~4px painted border so the
// sprite's cut-corner silhouette survives scaling; the rest of the slice is flat fill color.
const SLICE = 20;
const uiImage = new Image();
uiImage.src = UI_SHEET_URL;
const uiImageReady = new Promise((resolve) => {
    if (uiImage.complete && uiImage.naturalWidth > 0)
        resolve();
    else
        uiImage.addEventListener("load", () => resolve(), { once: true });
});
function drawNineSlice(ctx, rect, destW, destH) {
    // Clamp the slice to the actual box (rather than inflating the box to fit the slice) so
    // panels shorter/narrower than 2×SLICE — e.g. the collapsed leaderboard header — still get
    // their full border drawn within the canvas bounds instead of having it clipped off.
    const s = Math.max(1, Math.min(SLICE, Math.floor(destW / 2), Math.floor(destH / 2)));
    const { x: sx, y: sy, width: sw, height: sh } = rect;
    const dw = destW;
    const dh = destH;
    const midSW = sw - 2 * s;
    const midSH = sh - 2 * s;
    const midDW = dw - 2 * s;
    const midDH = dh - 2 * s;
    ctx.imageSmoothingEnabled = false;
    // Corners (drawn at native scale)
    ctx.drawImage(uiImage, sx, sy, s, s, 0, 0, s, s);
    ctx.drawImage(uiImage, sx + sw - s, sy, s, s, dw - s, 0, s, s);
    ctx.drawImage(uiImage, sx, sy + sh - s, s, s, 0, dh - s, s, s);
    ctx.drawImage(uiImage, sx + sw - s, sy + sh - s, s, s, dw - s, dh - s, s, s);
    // Edges (stretched along one axis)
    if (midDW > 0) {
        ctx.drawImage(uiImage, sx + s, sy, midSW, s, s, 0, midDW, s);
        ctx.drawImage(uiImage, sx + s, sy + sh - s, midSW, s, s, dh - s, midDW, s);
    }
    if (midDH > 0) {
        ctx.drawImage(uiImage, sx, sy + s, s, midSH, 0, s, s, midDH);
        ctx.drawImage(uiImage, sx + sw - s, sy + s, s, midSH, dw - s, s, s, midDH);
    }
    // Fill (stretched both axes)
    if (midDW > 0 && midDH > 0) {
        ctx.drawImage(uiImage, sx + s, sy + s, midSW, midSH, s, s, midDW, midDH);
    }
}
/**
 * Renders a nine-patch panel background behind `el`'s existing content.
 *
 * `el` must already have a non-static `position` and a non-auto `z-index` (every panel in this
 * codebase does) so it establishes its own stacking context — that's what lets the canvas layer
 * sit at z-index -1 without escaping behind unrelated page content.
 */
export function applyPanel(el, variant) {
    el.style.background = "transparent";
    const canvas = document.createElement("canvas");
    canvas.style.cssText = `
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    pointer-events: none;
  `;
    el.insertBefore(canvas, el.firstChild);
    const rect = PANEL_SOURCES[variant];
    const ctx = canvas.getContext("2d");
    if (!ctx)
        return;
    function render() {
        const w = el.clientWidth;
        const h = el.clientHeight;
        if (w === 0 || h === 0 || !ctx)
            return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        drawNineSlice(ctx, rect, w, h);
    }
    void uiImageReady.then(render);
    new ResizeObserver(render).observe(el);
}
